<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <!-- Header Section -->
    <Header />

    <!-- Main Content -->
    <main :class="mainContentClass">
      <div class="main-header">
        <div>
          <h1 class="main-title">Subscriptions</h1>
          <p class="subheading">
            Choose a subscription plan that fits your needs. Minutes renew
            automatically each month.
          </p>
        </div>
      </div>

      <Divider />
      <br />

      <!-- Subscription Plans -->
      <div class="plans-container">
        <div
          v-for="plan in subscriptionPlans"
          :key="plan.id"
          class="plan-card"
          :class="{
            selected: selectedPlan === plan.id,
            active: activePlanId === plan.id,
          }"
          @click="selectPlan(plan.id)"
        >
          <div
            v-if="activePlanId === plan.id || plan.badge"
            class="plan-badge"
            :class="{ 'active-badge': activePlanId === plan.id }"
          >
            {{ activePlanId === plan.id ? "Active" : plan.badge }}
          </div>
          <h2 class="plan-name">{{ plan.name }}</h2>
          <div class="plan-price">
            <span class="currency">$</span>
            <span class="amount">{{ plan.price }}</span>
            <span class="period">/month</span>
          </div>
          <p class="plan-description">{{ plan.description }}</p>
          <div class="plan-features">
            <div
              v-for="(feature, idx) in plan.features"
              :key="idx"
              class="feature-item"
            >
              <vue-feather type="check" size="18" style="color: #000" />
              <span>{{ feature }}</span>
            </div>
          </div>
          <button
            class="select-btn"
            :class="{
              'cancel-btn': activePlanId === plan.id,
              'disabled-btn': isUpgradeDisabled(plan.id),
            }"
            :disabled="isUpgradeDisabled(plan.id)"
            @click.stop="handleSubscribe(plan.id)"
          >
            {{ getPlanCta(plan.id) }}
          </button>
        </div>
      </div>

      <!-- Info Section -->
      <div class="info-section">
        <h3>Subscription Details</h3>
        <ul class="info-list">
          <li>Minutes are delivered automatically each month</li>
          <li>Cancel or modify your subscription anytime</li>
          <li>Unused minutes do not roll over to next month</li>
          <li>No long-term commitment required</li>
        </ul>
      </div>
    </main>

    <!-- Cancel Subscription Confirmation Modal -->
    <CancelSubscriptionModal
      :isOpen="showCancelModal"
      :planName="planToCancelName"
      :isLoading="isCancelling"
      @close="closeCancelModal"
      @confirm="handleConfirmCancel"
    />
  </div>
</template>

<script>
import Header from "@/components/organisms/Header.vue";
import Divider from "@/components/organisms/Divider.vue";
import CancelSubscriptionModal from "@/components/organisms/modal/CancelSubscriptionModal.vue";
import { useSessionStore } from "@/stores/session";
import { isMobile } from "@/utils/helper";
import { subscriptionService } from "@/services/subscriptionService";
import { db } from "@/plugins/firebase";
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
} from "firebase/firestore";

export default {
  data() {
    return {
      selectedPlan: null,
      subscriptionPlans: [],
      isLoadingPlans: true,
      activePlanId: null,
      activeSubscriptionId: null,
      activeSubscriptionStripeId: null,
      unsubscribeActive: null,
      showCancelModal: false,
      planToCancel: null,
      isCancelling: false,
    };
  },
  components: {
    Header,
    Divider,
    CancelSubscriptionModal,
  },
  computed: {
    mainContentClass() {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    },
    hasActivePlan() {
      return this.activePlanId !== null;
    },
    planToCancelName() {
      if (!this.planToCancel) return "";
      const plan = this.subscriptionPlans.find(
        (p) => p.id === this.planToCancel
      );
      return plan ? plan.name : "";
    },
  },
  async mounted() {
    await this.loadPlans();
    this.startSubscriptionListener();
  },
  unmounted() {
    if (this.unsubscribeActive) {
      this.unsubscribeActive();
      this.unsubscribeActive = null;
    }
  },
  methods: {
    isUpgradeDisabled(planId) {
      return this.hasActivePlan && this.activePlanId !== planId;
    },
    async loadPlans() {
      try {
        this.isLoadingPlans = true;
        this.subscriptionPlans = await subscriptionService.getPlans();
      } catch (error) {
        console.error("Error loading plans:", error);
      } finally {
        this.isLoadingPlans = false;
      }
    },
    selectPlan(planId) {
      this.selectedPlan = planId;
    },
    async handleSubscribe(planId) {
      // Prevent upgrade if there's an active plan
      if (this.hasActivePlan && this.activePlanId !== planId) {
        return;
      }

      try {
        const sessionStore = useSessionStore();
        const userId = sessionStore.userId;

        const plan = this.subscriptionPlans.find((p) => p.id === planId);
        if (!plan) {
          throw new Error("Plan not found");
        }

        // If canceling, show confirmation modal
        if (this.activePlanId === planId) {
          this.planToCancel = planId;
          this.showCancelModal = true;
          return;
        }

        // Otherwise, proceed with subscription
        await subscriptionService.handleSubscription(
          plan,
          userId,
          this.activeSubscriptionId || null,
          this.activeSubscriptionStripeId || null
        );
      } catch (error) {
        console.error("Error subscribing:", error);
        alert("Error subscribing. Please try again.");
      }
    },
    closeCancelModal() {
      this.showCancelModal = false;
      this.planToCancel = null;
    },
    async handleConfirmCancel() {
      if (!this.planToCancel) return;

      this.isCancelling = true;
      try {
        const sessionStore = useSessionStore();
        const userId = sessionStore.userId;

        await subscriptionService.cancelSubscription(
          userId,
          this.activeSubscriptionId || null,
          this.activeSubscriptionStripeId || null,
          "user_requested"
        );

        // Close modal after successful cancellation
        this.closeCancelModal();
      } catch (error) {
        console.error("Error canceling subscription:", error);
        alert("Error canceling subscription. Please try again.");
      } finally {
        this.isCancelling = false;
      }
    },
    startSubscriptionListener() {
      const sessionStore = useSessionStore();
      const userId = sessionStore.userId;
      if (!userId) return;

      const subsRef = collection(db, "subscriptions");
      const q = query(
        subsRef,
        where("userId", "==", userId),
        where("status", "==", "active"),
        limit(1)
      );

      this.unsubscribeActive = onSnapshot(q, (snapshot) => {
        if (snapshot.empty) {
          this.activePlanId = null;
          this.activeSubscriptionId = null;
          this.activeSubscriptionStripeId = null;
          return;
        }
        const doc = snapshot.docs[0];
        const data = doc.data();
        const planId = data.planId || data.plan || data.tierId || null;
        this.activePlanId = planId;
        this.activeSubscriptionId = doc.id;
        this.activeSubscriptionStripeId = data.stripeSubscriptionId || null;
      });
    },
    getPlanCta(planId) {
      if (this.activePlanId === planId) return "Cancel";
      if (this.activePlanId) return "Upgrade";
      return "Subscribe";
    },
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
}

.main-header {
  margin-bottom: 2rem;
}

.main-title {
  font-size: 3rem;
  color: #000;
  margin-bottom: 1rem;
}

.subheading {
  font-size: 1.1rem;
  color: #6b7280;
  line-height: 1.6;
}

.main-content {
  padding: 8rem 10rem;
}

.mobile-main-content {
  margin-top: 20px;
  padding: 20px;
}

.web-main-content {
  padding: 8rem 10rem;
}

.plans-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-bottom: 48px;
}

.plan-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  border: 2px solid #f2f5f9;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.plan-card:hover {
  border-color: #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.plan-card.selected {
  border: 2px solid #000;
}

.plan-card.active {
  border: 2px solid #000;
}

.plan-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #000;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.plan-name {
  font-size: 1.5rem;
  color: #000;
  margin-bottom: 16px;
  font-weight: 600;
}

.plan-price {
  font-size: 2.5rem;
  color: #000;
  margin-bottom: 8px;
}

.currency {
  font-size: 1.5rem;
  vertical-align: top;
}

.amount {
  font-weight: 300;
}

.period {
  font-size: 1rem;
  color: #6b7280;
  vertical-align: baseline;
}

.plan-description {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
}

.plan-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #111827;
}

.select-btn {
  width: 100%;
  padding: 14px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.select-btn:hover:not(:disabled) {
  background-color: #333;
}

.select-btn.disabled-btn,
.select-btn:disabled {
  background-color: #9ca3af;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.6;
}

.cancel-btn {
  background-color: #ef4444;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #dc2626;
}

.active-badge {
  background-color: #10b981;
}

.active-badge:hover {
  background-color: #059669;
}

.info-section {
  margin-top: 48px;
  padding: 32px;
  background-color: #f9fafb;
  border-radius: 12px;
}

.info-section h3 {
  font-size: 18px;
  margin-bottom: 20px;
  color: #111827;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-list li {
  position: relative;
  padding-left: 24px;
  margin-bottom: 12px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.5;
}

.info-list li:before {
  content: "•";
  position: absolute;
  left: 8px;
  color: #9ca3af;
}

/* Responsive */
@media (max-width: 1024px) {
  .plans-container {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .web-main-content {
    padding: 4rem 2rem;
  }
}

@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }

  .plan-card {
    padding: 24px;
  }

  .plan-price {
    font-size: 2rem;
  }

  .web-main-content {
    padding: 8rem 2rem;
  }
}
</style>
